"use client";
import React, { useCallback, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import Event from "../event";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Navigation, Pagination } from "swiper/modules";
import { Flex } from "antd";
import { useRef } from "react";
import styles from "./event-section.module.css";
import SliderNavButtons from "@/components/common/SliderNavButtons";
import CMSLink from "@/components/common/CMSLink";
import CardRowSkeleton from "@/components/CMS/CardRowSkeleton";
import { api } from "@/lib/api";
import { bindDotWindow } from "@/lib/swiperDots";

function EventSlider({
  title,
  data = [],
  badge = null,
  button = null,
  city,
  allowEmpty = false,
  emptyMessage = null,
  // { endpoint, params, lastPage, defaultStatus } — when set, later pages
  // are fetched as the user reaches the end of the slider.
  loadMore = null,
}) {
  const [mounted, setMounted] = useState(false);
  const [swiperInstance, setSwiperInstance] = useState(null);
  const [paginationEl, setPaginationEl] = useState(null);
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  // Real (de-duplicated) event count, so loop-duplicated slides share dots.
  const itemCountRef = useRef(0);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  React.useEffect(() => {
    if (swiperInstance && paginationEl && !swiperInstance.destroyed) {
      swiperInstance.params.pagination.el = paginationEl;
      if (swiperInstance.pagination) {
        swiperInstance.pagination.destroy();
        swiperInstance.pagination.init();
        swiperInstance.pagination.render();
        swiperInstance.pagination.update();
      }
      return bindDotWindow(swiperInstance, paginationEl, {
        itemCount: () => itemCountRef.current,
      });
    }
  }, [swiperInstance, paginationEl]);

  const [extraEvents, setExtraEvents] = useState([]);
  const [loadedPage, setLoadedPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingMoreRef = useRef(false);

  const baseData = Array.isArray(data) ? data : [];
  const hasMorePages = Boolean(loadMore) && loadedPage < loadMore.lastPage;

  const fetchNextPage = useCallback(async () => {
    if (!loadMore || loadingMoreRef.current || loadedPage >= loadMore.lastPage)
      return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    const nextPage = loadedPage + 1;
    try {
      const { data: res, ok } = await api.get(loadMore.endpoint, {
        ...loadMore.params,
        page: nextPage,
      });
      const items = res?.data?.data ?? res?.data;
      if (ok && Array.isArray(items)) {
        const status = loadMore.defaultStatus || "ongoing";
        setExtraEvents((prev) => [
          ...prev,
          ...items.map((evt) => ({
            ...evt,
            image:
              evt?.image ||
              evt?.cover_image ||
              evt?.banner_image ||
              evt?.feature_image,
            status: evt?.status || status,
          })),
        ]);
        setLoadedPage(nextPage);
      }
    } catch (err) {
      console.error("[EventSlider] Error loading more events:", err);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [loadMore, loadedPage]);

  // De-dupe in case the API's page boundaries shift between requests.
  const seenIds = new Set();
  data = [...baseData, ...extraEvents].filter((evt) => {
    const k = evt?.id ?? evt?.slug;
    if (k == null) return true;
    if (seenIds.has(k)) return false;
    seenIds.add(k);
    return true;
  });

  itemCountRef.current = data.length;

  const hasEvents = data.length > 0;
  if (!hasEvents && !allowEmpty) return null;

  const isPast = title?.toLowerCase()?.includes("past");
  const isOngoing = title?.toLowerCase()?.includes("ongoing");
  const emptyTitle = isPast
    ? "No Past Events"
    : isOngoing
      ? "No Ongoing Events"
      : "No Upcoming Events";
  const defaultEmptyDesc = isPast
    ? "There are no past events listed for this artist yet."
    : "Stay tuned! New events will be announced soon.";

  return (
    <section className="flex flex-col gap-[20px] my-6 md:my-8">
      <Flex vertical gap={20}>
        <div className={styles.sectionHeader}>
          <div className={styles.headerTitleWrap}>
            {title && <h2 className={styles.title}>{title}</h2>}
            {badge ? (
              <div className={styles.badge}>
                {React.isValidElement(badge) ? (
                  badge
                ) : typeof badge === "string" &&
                  (badge.startsWith("http") ||
                    badge.startsWith("/") ||
                    badge.startsWith("data:")) ? (
                  <img
                    src={badge}
                    alt={title || "Badge"}
                    className={styles.badgeImg}
                    loading="lazy"
                  />
                ) : typeof badge === "object" && badge?.url ? (
                  <img
                    src={badge.url}
                    alt={title || "Badge"}
                    className={styles.badgeImg}
                    loading="lazy"
                  />
                ) : (
                  badge
                )}
              </div>
            ) : null}
            {button && hasEvents && (
              <CMSLink
                config={button}
                city={city}
                className={`${styles.seeAllLink} !hidden md:!inline-flex items-center pt-[10px]`}
              />
            )}
          </div>

          <div className={styles.headerActions}>
            {button && hasEvents && (
              <CMSLink
                config={button}
                city={city}
                className={`${styles.seeAllLink} !inline-flex md:!hidden items-center`}
              />
            )}
            {hasEvents && data.length > 1 ? (
              <SliderNavButtons
                prevRef={prevRef}
                nextRef={nextRef}
                swiper={swiperInstance}
                itemsCount={data.length}
              />
            ) : null}
          </div>
        </div>

        {!hasEvents ? (
          <div className="flex flex-col items-center justify-center p-8 sm:p-12 rounded-2xl border border-dashed border-[#CBD5E1] bg-[#F8FAFC] text-center my-1">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[#EEF2F6] flex items-center justify-center mb-3 text-3xl sm:text-4xl shadow-sm">
              {isPast ? "📜" : "🎭"}
            </div>
            <p className="text-base sm:text-lg font-bold text-[#021526] mb-1">
              {emptyTitle}
            </p>
            <p className="text-xs sm:text-sm text-[#64748B] max-w-md">
              {emptyMessage || defaultEmptyDesc}
            </p>
          </div>
        ) : !mounted ? (
          <CardRowSkeleton count={3} fixedHeight={200} radius={16} gap={24} />
        ) : (
          <>
            <Swiper
              modules={[Navigation, Pagination]}
              onSwiper={setSwiperInstance}
              onReachEnd={hasMorePages ? fetchNextPage : undefined}
              onSlideChange={(swiper) => {
                // Prefetch a little before the very last slide so the next
                // page is usually ready by the time the user gets there.
                if (
                  hasMorePages &&
                  swiper.activeIndex + 6 >= swiper.slides.length
                ) {
                  fetchNextPage();
                }
              }}
              onInit={(swiper) => {
                swiper.params.navigation.prevEl = prevRef.current;
                swiper.params.navigation.nextEl = nextRef.current;
                swiper.navigation.init();
                swiper.navigation.update();
              }}
              navigation={{
                prevEl: prevRef.current,
                nextEl: nextRef.current,
              }}
              pagination={
                data.length > 1
                  ? {
                    el: paginationEl,
                    clickable: true,
                  }
                  : false
              }
              spaceBetween={24}
              loop={!isPast && !loadMore && data.length > 3}
              slidesPerView={2}
              breakpoints={{
                480: {
                  slidesPerView: 2.5,
                },
                567: {
                  slidesPerView: 2.8,
                },
                768: {
                  slidesPerView: 3,
                },
                1024: {
                  slidesPerView: 3,
                },
              }}
              className={`w-full ${styles.eventSwiper}`}
            >
              {(!isPast && !loadMore && data.length > 3 && data.length <= 6
                ? [...data, ...data]
                : data
              ).map(
                (
                  {
                    id,
                    name,
                    slug,
                    start_time,
                    start_date,
                    end_time,
                    end_date,
                    yet_to_be_announced,
                    venue,
                    timezone,
                    image,
                    feature_image,
                    feature_images,
                    banner,
                    price,
                    currency,
                    amount,
                    max_price,
                    sold_out,
                    organizer,
                    status,
                    is_contest,
                    description,
                    is_third_party,
                    source_platform_logo,
                  },
                  index,
                ) => (
                  <SwiperSlide key={index} className="!h-auto">
                    <Event
                      name={name}
                      organizer={organizer}
                      slug={slug}
                      start_date={start_date}
                      start_time={start_time}
                      max_price={max_price}
                      yet_to_be_announced={yet_to_be_announced}
                      venue={venue}
                      image={image || feature_image || banner || feature_images}
                      feature_image={feature_image}
                      feature_images={feature_images}
                      banner={banner}
                      price={price}
                      timezone={timezone}
                      currency={currency}
                      amount={amount}
                      sold_out={sold_out}
                      end_date={end_date}
                      end_time={end_time}
                      status={status}
                      is_contest={is_contest}
                      description={description}
                      is_third_party={is_third_party}
                      source_platform_logo={source_platform_logo}
                    />
                  </SwiperSlide>
                ),
              )}
              {loadingMore && (
                <SwiperSlide className="!h-auto">
                  <div className="h-full min-h-[280px] rounded-2xl bg-[#F1F5F9] animate-pulse" />
                </SwiperSlide>
              )}
            </Swiper>
            {hasEvents && data.length > 1 && (
              <div
                ref={setPaginationEl}
                className="swiper-pagination mobile-slider-pagination"
              />
            )}
          </>
        )}
      </Flex>
    </section>
  );
}

export default EventSlider;
